import { Grid, Paper, Typography, Box, Stack } from '@mui/material';
import { PedidoCompletoEDClienteType } from '../../types/PedidoTypes';
import { formatarData, formatarDinheiro, getTipoStatus } from '../../helper';
import { DataGrid, GridColDef, ptBR } from '@mui/x-data-grid';
import PaymentsIcon from '@mui/icons-material/Payments';

type Props = {
  pedido: null | PedidoCompletoEDClienteType;
  imprimindo?: boolean;
};

const ResumoPagamentoPedidoCliente: React.FC<Props> = (props) => {
  const { pedido, imprimindo, ...other } = props;

  if (!pedido) return null;

  const parcelas = pedido.parcelas;

  const columns: GridColDef[] = [
    {
      field: 'parcela',
      headerName: 'Parcela',
      flex: 1,
      renderCell: (params) => `${params.row.parcela + 1}º Parcela`
    },
    {
      field: 'valor',
      headerName: 'Valor',
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="700">
          R$ {formatarDinheiro(params.value)}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Tipo/Status',
      flex: 1,
      renderCell: (params) => getTipoStatus(params.row)
    },
    {
      field: 'vencimento',
      headerName: 'Vencimento',
      flex: 1,
      renderCell: (params) => formatarData(params.value)
    }
  ];

  return (
    <Box mt={2} {...other}>
      <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
        <PaymentsIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
        <Typography
          variant="subtitle2"
          fontWeight="800"
          color="textSecondary"
          sx={{ letterSpacing: 1 }}
        >
          PAGAMENTO
        </Typography>
      </Stack>

      <Stack spacing={2}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 3,
            bgcolor: '#f8fafc',
            borderStyle: 'dashed',
            borderColor: 'divider'
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" fontWeight="500" color="textSecondary">
              {pedido.condicaoPagamento === '00' ? 'TOTAL À VISTA' : 'TOTAL DO FINANCEIRO'}
            </Typography>
            <Typography variant="h6" fontWeight="900" color="primary.main">
              R${' '}
              {formatarDinheiro(pedido.totalBruto - pedido.descontoGeral + pedido.acrescimoGeral)}
            </Typography>
          </Box>
        </Paper>

        <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1 }}>
          {parcelas.map((parcela) => (
            <Paper
              key={parcela.parcela}
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                borderLeft: '4px solid',
                borderLeftColor: parcela.valor > 0 ? 'success.main' : 'divider'
              }}
            >
              <Grid container alignItems="center">
                <Grid item xs={7}>
                  <Typography variant="caption" color="textSecondary" display="block">
                    {parcela.parcela + 1}º PARCELA
                  </Typography>
                  <Typography variant="body2" fontWeight="700">
                    Venc: {formatarData(parcela.vencimento)}
                  </Typography>
                </Grid>
                <Grid item xs={5} textAlign="right">
                  <Typography variant="body2" fontWeight="900" color="textPrimary">
                    R$ {formatarDinheiro(parcela.valor)}
                  </Typography>
                  <Box mt={0.5}>{getTipoStatus(parcela)}</Box>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Box>

        {parcelas.length !== 0 && (
          <Box sx={{ display: { xs: 'none', md: 'block' }, height: 'auto', width: '100%' }}>
            <DataGrid
              rows={parcelas}
              columns={columns}
              autoHeight
              getRowId={(row) => row.parcela}
              density="compact"
              hideFooter
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: '#f1f5f9',
                  borderRadius: 1
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #f1f5f9'
                }
              }}
              localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default ResumoPagamentoPedidoCliente;
